import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { RichEditor } from "@/components/blog/RichEditor";
import { blogsApi } from "@/lib/apiClient";
import { useAppState } from "@/state/AppStateProvider";
import {
  ArrowLeft,
  Save,
  Send,
  Sparkles,
  Image as ImageIcon,
  Trash2,
  Linkedin,
  Twitter,
  Youtube,
  BookOpen,
  Clock,
  Globe,
  GraduationCap,
  Link as LinkIcon,
  FileText,
} from "lucide-react";

const SOURCE_KIND = {
  web: { label: "web", Icon: Globe },
  academic: { label: "paper", Icon: GraduationCap },
  url: { label: "link", Icon: LinkIcon },
  file: { label: "file", Icon: FileText },
};

function plainTextFromHtml(html = "") {
  return String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&mdash;|&#8212;/g, "-")
    .replace(/&ndash;|&#8211;/g, "-")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanBlogInstructionTitle(title = "") {
  return String(title || "")
    .replace(/\s+/g, " ")
    .replace(/^write\s+(?:a\s+)?(?:long[-\s]?form\s+)?(?:blog\s+)?post\s+(?:on|about)\s+/i, "")
    .replace(/^create\s+(?:a\s+)?(?:long[-\s]?form\s+)?(?:blog\s+)?post\s+(?:on|about)\s+/i, "")
    .replace(/\b(be technically detailed|shock the reader|make it shocking|long form|long-form)\b\.?/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+\./g, ".")
    .trim();
}

function buildBlogCoverPrompt(blog) {
  if (blog?.image_prompt?.trim()) return blog.image_prompt.trim();
  const title = cleanBlogInstructionTitle(blog?.seo_title || blog?.title || "this article");
  const excerpt = plainTextFromHtml(blog?.excerpt || "");
  const bodyLead = plainTextFromHtml(blog?.body_html || "").slice(0, 260);
  const visualBrief = excerpt || bodyLead || title;
  return [
    `Wide 16:9 editorial hero illustration for a long-form article titled "${title}".`,
    `Visual brief: ${visualBrief}.`,
    "Create a cinematic, magazine-quality scene with a clear central subject, strong composition, realistic lighting, and enough negative space for cropping.",
    "No text, captions, logos, UI screens, maps with labels, or watermarks.",
  ].join(" ");
}

function isDefaultBlogCoverPrompt(prompt = "") {
  const p = String(prompt || "").trim();
  return !p || /^Conceptual editorial illustration about /i.test(p) || /^Wide 16:9 editorial hero illustration for /i.test(p);
}
import { STATUS_LABELS } from "@/lib/constants";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function BlogStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const aiStartedRef = useRef(false);
  const { state, updateBlog, deleteBlog, generateBlog, generateImage, uploadImage } = useAppState();

  const blog = useMemo(() => (state.blogs || []).find((b) => b.id === id), [state.blogs, id]);

  const [aiRunning, setAiRunning] = useState(false);
  const [aiPhase, setAiPhase] = useState(0);
  const [aiPhases, setAiPhases] = useState([]);
  const [wordCount, setWordCount] = useState(blog?.word_count || 0);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverGenBusy, setCoverGenBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const coverProvider = "free"; // Cloudflare Workers AI (only image provider)
  const [coverPrompt, setCoverPrompt] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(blog?.updated_at);

  // Handle AI generation kicked off from BlogList — real long-form generation
  // via the user's configured model. The phase bar animates while we await.
  useEffect(() => {
    const ns = location.state;
    if (ns?.ai && blog && !aiStartedRef.current) {
      aiStartedRef.current = true;
      // Honest phase labels: only show steps this run actually performs.
      const phases = buildBlogPhases(ns);
      setAiPhases(phases);
      setAiRunning(true);
      setAiPhase(1);
      const timers = phases.slice(1).map((_, i) =>
        setTimeout(() => setAiPhase(i + 2), (i + 1) * 1100),
      );
      (async () => {
        const result = await generateBlog(blog.id, {
          topic: ns.topic || blog.title,
          tone: ns.tone || "Adam voice",
          audience: ns.audience || "",
          context: ns.context || "",
          research_mode: ns.research || "none",
          research_provider: ns.research_provider || "ollama",
          research_focus: ns.research_focus || "general",
          provider_id: ns.provider_id,
          model: ns.model,
          brand_id: ns.brand_id,
          voice_id: ns.voice_id,
          style_skill_id: ns.style_skill_id,
          goal: ns.goal,
          cta: ns.cta,
          cta_type: ns.cta_type,
          seo: ns.seo,
          discovery_mode: ns.discovery_mode,
          hashtag_mode: ns.hashtag_mode,
          destination_url: ns.destination_url,
          sources: ns.sources || [],
        });
        timers.forEach(clearTimeout);
        setAiPhase(phases.length + 1);
        setLastSavedAt(new Date().toISOString());
        setTimeout(() => {
          setAiRunning(false);
          // Clear router state so a refresh doesn't re-trigger generation.
          navigate(location.pathname, { replace: true, state: null });
          if (result?.ok) toast.success("Draft ready — edit freely.");
          else toast.error(`Generation failed: ${result?.message || "unknown error"}. Write manually or retry.`);
        }, 400);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blog?.id, location.state]);

  // Seed/update the cover image prompt from generated blog metadata. AI-created
  // blogs briefly use the raw user instruction as title, so only auto-rewrite
  // prompts that still look like our default template.
  useEffect(() => {
    if (!blog || !isDefaultBlogCoverPrompt(coverPrompt)) return;
    setCoverPrompt(buildBlogCoverPrompt(blog));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blog?.id, blog?.title, blog?.seo_title, blog?.excerpt, blog?.body_html, blog?.image_prompt]);

  const onEditorChange = useCallback(
    (html) => {
      if (!blog) return;
      updateBlog(blog.id, { body_html: html, word_count: wordCount });
      setLastSavedAt(new Date().toISOString());
    },
    [blog, updateBlog, wordCount],
  );

  const onCoverPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    if (file.size > 12 * 1024 * 1024) return toast.error("Image too large - keep it under 12MB");
    setCoverUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      // Persist via the backend (served by URL) instead of storing base64 in
      // the blog record — keeps the DB lean and the cover usable as a real URL.
      const res = await uploadImage(reader.result);
      setCoverUploading(false);
      if (res?.ok) { updateBlog(blog.id, { cover_image: res.url }); toast.success("Cover image set"); }
      else toast.error(`Upload failed: ${res?.message || "unknown error"}`);
    };
    reader.onerror = () => {
      setCoverUploading(false);
      toast.error("Could not load image");
    };
    reader.readAsDataURL(file);
  };

  const onPublishToSite = async () => {
    setPublishing(true);
    const pending = toast.loading("Publishing to your site…");
    try {
      const res = await blogsApi.publish(blog.id);
      updateBlog(blog.id, { status: "published" });
      if (res?.url) {
        toast.success(`Published → ${res.url}`, { id: pending });
      } else {
        toast.success("Published to your site", { id: pending });
      }
    } catch (err) {
      // Backend returns a clear message (no endpoint configured, HTTP error, etc.)
      toast.error(err.message || "Publish failed", { id: pending });
    } finally {
      setPublishing(false);
    }
  };

  const onGenerateCover = async () => {
    const prompt = (coverPrompt || "").trim();
    if (!prompt) return toast.error("Add an image prompt first");
    setCoverGenBusy(true);
    const pending = toast.loading("Generating cover image…");
    try {
      // Blog covers are 16:9 (wide) to match the article hero layout.
      const result = await generateImage(prompt, coverProvider, "wide");
      if (result?.ok) {
        updateBlog(blog.id, { cover_image: result.url });
        toast.success("Cover image generated", { id: pending });
      } else {
        toast.error(`Image generation failed: ${result?.message || "unknown error"}`, { id: pending });
      }
    } finally {
      setCoverGenBusy(false);
    }
  };

  if (!blog) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="gap-1.5" data-testid="blog-back">
          <Link to="/blog"><ArrowLeft className="h-4 w-4" /> Back to Blog Studio</Link>
        </Button>
        <div className="rounded-xl border p-10 text-center" style={{ borderColor: "var(--border-subtle)" }}>
          <p className="text-sm text-1">Blog not found.</p>
          <p className="mt-1 text-xs text-muted-foreground">It may have been deleted. Start a new one from the studio.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="blog-studio-page">
      {/* Compact header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-1" data-testid="blog-back">
            <Link to="/blog"><ArrowLeft className="h-4 w-4" /> Blog Studio</Link>
          </Button>
          <span className="text-[12px] text-muted-foreground">/</span>
          <p className="truncate text-[13px] text-1">{blog.title}</p>
          <StatusBadge status={blog.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden text-[11px] text-muted-foreground md:inline">
            <Clock className="mr-1 inline h-3 w-3" /> Auto-saved · {wordCount} words
          </span>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => toast.success("Draft saved")} data-testid="blog-save">
            <Save className="h-4 w-4" /> Save
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => { updateBlog(blog.id, { status: "needs_review" }); toast.success("Submitted for review"); }} data-testid="blog-submit" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
            <Send className="h-4 w-4" /> Submit for review
          </Button>
        </div>
      </div>

      {/* AI generation overlay */}
      <AnimatePresence>
        {aiRunning && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="relative overflow-hidden rounded-2xl border p-5 inner-highlight"
            style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
            data-testid="blog-ai-overlay"
          >
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-80" style={{ backgroundImage: "radial-gradient(420px 200px at 10% 0%, rgba(143, 224, 74,0.15), transparent 60%), radial-gradient(420px 200px at 90% 0%, rgba(18, 147, 79,0.18), transparent 60%)" }} />
            <div className="relative flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border" style={{ borderColor: "rgba(143, 224, 74,0.30)", backgroundColor: "rgba(143, 224, 74,0.06)" }}>
                <Sparkles className="h-4 w-4 animate-pulse text-[color:var(--accent-cyan)]" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-1">Agent is drafting your blog post…</p>
                <p className="text-[12.5px] text-muted-foreground">{aiPhase > aiPhases.length ? "Done." : (aiPhases[aiPhase - 1] || "Working")}</p>
              </div>
              <span className="font-mono text-[11px] text-muted-foreground">{Math.min(aiPhase, aiPhases.length)}/{aiPhases.length}</span>
            </div>
            <div className="relative mt-4 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${aiPhases.length}, minmax(0, 1fr))` }}>
              {aiPhases.map((label, i) => (
                <span
                  key={label}
                  className="h-1 rounded-full transition-colors"
                  style={{ backgroundColor: aiPhase > i ? "var(--accent-cyan)" : "var(--border-subtle)" }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Main: editor */}
        <section className="xl:col-span-8">
          <div className="rounded-2xl border bg-card inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            {/* Title */}
            <div className="border-b p-6" style={{ borderColor: "var(--border-subtle)" }}>
              <input
                type="text"
                value={blog.title}
                onChange={(e) => updateBlog(blog.id, { title: e.target.value })}
                placeholder="Untitled blog post"
                data-testid="blog-title"
                className="w-full border-0 bg-transparent p-0 text-[28px] font-semibold leading-[1.2] tracking-[-0.01em] text-1 outline-none placeholder:text-muted-foreground/40 focus:outline-none"
              />
              <Textarea
                value={blog.excerpt || ""}
                onChange={(e) => updateBlog(blog.id, { excerpt: e.target.value })}
                rows={2}
                placeholder="A one-sentence excerpt — used as preview text + meta description fallback."
                data-testid="blog-excerpt"
                className="mt-3 resize-none border-0 bg-transparent p-0 text-[14.5px] leading-[1.6] text-muted-foreground focus-visible:ring-0"
              />
            </div>

            {/* Rich editor */}
            <div className="px-6 pb-10 pt-2">
              <RichEditor
                value={blog.body_html}
                onChange={onEditorChange}
                onWordCount={(n) => { setWordCount(n); updateBlog(blog.id, { word_count: n }); }}
              />
            </div>
          </div>

          {/* Footer meta */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
            <span>Auto-save · last edit {lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
            <button type="button" onClick={() => { if (window.confirm("Delete this draft?")) { deleteBlog(blog.id); navigate("/blog"); toast.message("Draft deleted"); } }} className="inline-flex items-center gap-1 hover:text-[color:var(--accent-red,#FF7A8A)]" data-testid="blog-delete">
              <Trash2 className="h-3 w-3" /> Delete draft
            </button>
          </div>
        </section>

        {/* Right sidebar */}
        <aside className="space-y-4 xl:col-span-4">
          {/* Cover image */}
          <div className="rounded-2xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1">Cover image</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">Shown on the blog index + social previews.</p>
            <label
              className={cn(
                "relative mt-3 flex aspect-[16/9] w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition-colors",
                "hover:border-[color:var(--accent-cyan)]/45",
              )}
              style={{ borderColor: "var(--border-subtle)", backgroundColor: blog.cover_image ? "transparent" : "var(--surface-2)" }}
              data-testid="blog-cover-dropzone"
            >
              {blog.cover_image ? (
                <img src={blog.cover_image} alt="Cover" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-center">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  <p className="text-[12px] text-muted-foreground">{coverUploading ? "Uploading…" : "Click or drop an image"}</p>
                  <p className="text-[10.5px] text-muted-foreground/70">PNG, JPG · up to 5MB</p>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={onCoverPick} data-testid="blog-cover-input" />
            </label>
            <Textarea
              value={coverPrompt}
              onChange={(e) => setCoverPrompt(e.target.value)}
              rows={2}
              placeholder="Describe the image you want…"
              data-testid="blog-cover-prompt"
              className="mt-3 resize-none text-[12.5px]"
              style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Describe a scene, not words to render. Free model avoids text.</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={onGenerateCover} disabled={coverGenBusy} data-testid="blog-cover-generate">
                {coverGenBusy ? <Clock className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {blog.cover_image ? "Regenerate" : "Generate with AI"}
              </Button>
              {blog.cover_image && (
                <button type="button" onClick={() => updateBlog(blog.id, { cover_image: null })} className="text-[11.5px] text-muted-foreground hover:text-1" data-testid="blog-cover-remove">
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Status + slug */}
          <div className="rounded-2xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1">Publishing</h3>
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Status</Label>
                <Select value={blog.status} onValueChange={(v) => updateBlog(blog.id, { status: v })}>
                  <SelectTrigger style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} data-testid="blog-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["draft", "needs_review", "approved", "scheduled", "published"].map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Slug</Label>
                <Input value={blog.slug || ""} onChange={(e) => updateBlog(blog.id, { slug: e.target.value })} data-testid="blog-slug" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                <p className="text-[11px] font-mono text-muted-foreground">/blog/{blog.slug || "untitled"}</p>
              </div>
              <div className="border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
                <Button size="sm" className="w-full gap-1.5" onClick={onPublishToSite} disabled={publishing} data-testid="blog-publish" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                  {publishing ? <Clock className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Publish to my site
                </Button>
                <p className="mt-1.5 text-[11px] text-muted-foreground">Sends this post to your configured blog endpoint. Set it up in <Link to="/settings" className="underline hover:text-1">Settings → Blog publishing</Link>.</p>
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="rounded-2xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1">SEO</h3>
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">SEO title</Label>
                <Input value={blog.seo_title || ""} onChange={(e) => updateBlog(blog.id, { seo_title: e.target.value })} data-testid="blog-seo-title" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                <p className="text-[11px] font-mono text-muted-foreground">{(blog.seo_title || "").length}/60</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Meta description</Label>
                <Textarea value={blog.seo_meta || ""} onChange={(e) => updateBlog(blog.id, { seo_meta: e.target.value })} rows={3} data-testid="blog-seo-meta" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                <p className="text-[11px] font-mono text-muted-foreground">{(blog.seo_meta || "").length}/160</p>
              </div>
            </div>
          </div>

          {/* Sources / provenance */}
          {blog.sources?.length > 0 && (
            <div className="rounded-2xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="blog-sources">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-1">
                <BookOpen className="h-4 w-4 text-[color:var(--accent-cyan)]" /> Sources
                <span className="text-[11px] font-normal text-muted-foreground">({blog.sources.length})</span>
              </h3>
              <p className="mt-1 text-[11.5px] text-muted-foreground">What this draft was grounded on.</p>
              <ul className="mt-3 space-y-2">
                {blog.sources.map((s, i) => {
                  const meta = SOURCE_KIND[s.kind] || { label: s.kind, Icon: LinkIcon };
                  const Icon = meta.Icon;
                  return (
                    <li key={i} className="flex items-start gap-2 text-[12px]">
                      <Icon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        {s.url
                          ? <a href={s.url} target="_blank" rel="noreferrer" className="line-clamp-2 text-[color:var(--accent-cyan)] hover:underline">{s.title}</a>
                          : <span className="line-clamp-2 text-1">{s.title}</span>}
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">· {meta.label}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Repurpose */}
          <div className="rounded-2xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1">Repurpose</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">Send this blog through the agent to produce platform cuts.</p>
            <div className="mt-3 flex flex-col gap-1.5">
              <RepurposeButton icon={Linkedin} label="Turn into LinkedIn post" onClick={() => navigate(`/repurpose?blog=${blog.id}&output=linkedin_post`)} testId="blog-repurpose-linkedin" />
              <RepurposeButton icon={Twitter} label="Turn into X thread" onClick={() => navigate(`/repurpose?blog=${blog.id}&output=x_thread`)} testId="blog-repurpose-thread" />
              <RepurposeButton icon={Youtube} label="Turn into Shorts script" onClick={() => navigate(`/repurpose?blog=${blog.id}&output=shorts_script`)} testId="blog-repurpose-shorts" />
              <RepurposeButton icon={BookOpen} label="Turn into newsletter blurb" onClick={() => navigate(`/repurpose?blog=${blog.id}&output=newsletter_blurb`)} testId="blog-repurpose-newsletter" />
            </div>
          </div>
        </aside>
      </div>
    </motion.div>
  );
}

// Build phase labels that reflect what this run actually does — no claims of
// steps (credibility filtering, self-critique) the engine doesn't perform.
function buildBlogPhases(ns = {}) {
  const phases = ["Analyzing your topic…"];
  if ((ns.sources && ns.sources.length) || (ns.context && ns.context.trim())) {
    phases.push("Reading your sources…");
  }
  if (ns.research && ns.research !== "none") {
    phases.push(ns.research_focus === "academic" ? "Searching academic papers…" : "Searching the web…");
  }
  phases.push("Drafting in your voice…");
  phases.push("Formatting and finishing…");
  return phases;
}

function RepurposeButton({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-[13px] text-foreground/85 transition-colors hover:border-[color:var(--accent-cyan)]/40 hover:text-1"
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}
    >
      <Icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-[color:var(--accent-cyan)]" />
      <span className="truncate">{label}</span>
    </button>
  );
}
